/**
 * Accessible tabs (WAI-ARIA tabs pattern, manual activation not needed for
 * static content so arrows activate immediately). Expects role="tab" buttons
 * with aria-controls pointing at role="tabpanel" elements inside the element.
 * Arrow Left/Right move and activate (mirrored in RTL), Home/End jump.
 */
class CrTabs extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
  }

  get #tabs() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[role="tab"]')));
  }

  /** @param {HTMLElement} tab @param {boolean} focus */
  select(tab, focus = false) {
    for (const other of this.#tabs) {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      other.tabIndex = selected ? 0 : -1;
      const panelId = other.getAttribute('aria-controls');
      const panel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;
      if (panel instanceof HTMLElement) panel.hidden = !selected;
    }
    if (focus) tab.focus();
  }

  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const tab = event.target.closest('[role="tab"]');
    if (tab instanceof HTMLElement && this.contains(tab)) this.select(tab);
  };

  #onKeydown = (event) => {
    if (!(event.target instanceof HTMLElement) || event.target.getAttribute('role') !== 'tab') return;
    const tabs = this.#tabs;
    const index = tabs.indexOf(event.target);
    if (index === -1) return;

    const rtl = getComputedStyle(this).direction === 'rtl';
    const next = rtl ? 'ArrowLeft' : 'ArrowRight';
    const previous = rtl ? 'ArrowRight' : 'ArrowLeft';

    let target;
    if (event.key === next) target = tabs[(index + 1) % tabs.length];
    else if (event.key === previous) target = tabs[(index - 1 + tabs.length) % tabs.length];
    else if (event.key === 'Home') target = tabs[0];
    else if (event.key === 'End') target = tabs[tabs.length - 1];
    if (!target) return;

    event.preventDefault();
    this.select(target, true);
  };
}

if (!customElements.get('cr-tabs')) customElements.define('cr-tabs', CrTabs);

// Theme editor: reveal the tab whose block is selected.
document.addEventListener('shopify:block:select', (event) => {
  if (!(event.target instanceof HTMLElement)) return;
  const tab = event.target.closest('[role="tab"]');
  const tabs = tab?.closest('cr-tabs');
  if (tab instanceof HTMLElement && tabs instanceof CrTabs) tabs.select(tab);
});
